import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControlLabel,
  Checkbox,
  Stack,
  Link,
  Divider,
} from "@mui/material";
import ButtonComponent from "../ui/ButtonComponent";

const SAAS_URL =
  process.env.REACT_APP_SAAS_AGREEMENT_URL ||
  "https://storage.googleapis.com/kinova/SOFTWARE%20AS%20A%20SERVICE%20AGREEMENT.pdf";

const PRIVACY_URL =
  process.env.REACT_APP_PRIVACY_POLICY_URL ||
  "https://storage.googleapis.com/kinova/KINOVA%20PRIVACY%20POLICY.pdf";

// Keep these explicit and bump when you update docs
const SAAS_VERSION = process.env.REACT_APP_SAAS_AGREEMENT_VERSION || "v1";
const PRIVACY_VERSION = process.env.REACT_APP_PRIVACY_POLICY_VERSION || "v1";

export default function CheckoutClickwrapModal({
  open,
  onClose,
  onContinue,
  isLoading,
}) {
  const [agreed, setAgreed] = React.useState(false);
  const [viewerUrl, setViewerUrl] = React.useState(null);

  const viewerRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) {
      setAgreed(false);
      setViewerUrl(null);
    }
  }, [open]);

  const openDoc = (url) => {
    setViewerUrl(url);

    // Wait for the iframe container to render, then scroll it into view smoothly
    window.setTimeout(() => {
      try {
        viewerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {
        // no-op (older browsers / edge cases)
      }
    }, 50);
  };

  const handleContinue = () => {
    if (!agreed) return;

    onContinue({
      accepted: true,
      saasVersion: SAAS_VERSION,
      privacyVersion: PRIVACY_VERSION,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Review & Accept Terms</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            To activate your subscription, you must review and accept the Kinova
            SaaS Agreement and Privacy Policy.
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Link component="button" type="button" onClick={() => openDoc(SAAS_URL)}>
              View SaaS Agreement
            </Link>
            <Typography variant="caption" color="text.secondary">
              (Version {SAAS_VERSION})
            </Typography>

            <Box sx={{ flex: "0 0 16px" }} />

            <Link component="button" type="button" onClick={() => openDoc(PRIVACY_URL)}>
              View Privacy Policy
            </Link>
            <Typography variant="caption" color="text.secondary">
              (Version {PRIVACY_VERSION})
            </Typography>
          </Stack>

          {viewerUrl ? (
            <>
              <Divider />
              <Box
                ref={viewerRef}
                sx={{
                  height: { xs: 460, sm: 620 },
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #e5e5e5",
                  bgcolor: "background.paper",
                }}
              >
                <iframe
                  title="Policy Viewer"
                  src={viewerUrl}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              </Box>
            </>
          ) : null}

          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={isLoading}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                I agree to the{" "}
                <Link component="button" type="button" onClick={() => openDoc(SAAS_URL)}>
                  SaaS Agreement
                </Link>{" "}
                and{" "}
                <Link component="button" type="button" onClick={() => openDoc(PRIVACY_URL)}>
                  Privacy Policy
                </Link>
                .
              </Typography>
            }
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <ButtonComponent
          label="Cancel"
          type="secondary"
          onClick={onClose}
          disabled={isLoading}
        />
        <ButtonComponent
          label="Continue to Checkout"
          type="primary"
          onClick={handleContinue}
          disabled={!agreed || isLoading}
          isLoading={isLoading}
        />
      </DialogActions>
    </Dialog>
  );
}